import type { Blocks } from '@w/content-blocks'
import { entryTrainingBlocks } from '../lexicon/entry-blocks'
import {
  ruleQid,
  type Language,
  type RuleBlock,
  type TranslationLanguage,
} from '../lexicon/lexicon-model'
import { ruleCardBlocks, ruleHasClozes, rulePracticeQid, type RuleMember } from '../lexicon/rule-blocks'
import type { PageContent } from '../server/db/schema'
import { groupPracticeQid, questionGroupCardBlocks } from './question-group-blocks'

export function pageBlocks(
  content: PageContent,
  translationLanguage?: TranslationLanguage,
): Blocks {
  if (content.kind === 'databaseRow' || content.kind === 'note' || content.kind === 'language')
    return content.children
  if (content.kind === 'lexiconEntry') {
    if ('children' in content && Array.isArray(content.children)) return content.children as Blocks
    return entryTrainingBlocks(content.entry, undefined, translationLanguage)
  }
  return []
}

// D4: the rule page's EXPLANATION prose is authored content (first child of
// the `rule` block, kept verbatim). The PRACTICE cloze is never authored — it
// is projected per rep from `ruleCardBlocks`, using derived (D1) class membership
// and a rep number sourced from real review state. `pageBlocks` stays sync and
// content-only for non-training call sites (page viewer, cards admin list);
// this is the seam the actual study render path uses.
export type RuleProjectionDeps = {
  members: (
    language: Language,
    inflectionClass: string,
    rep: number,
  ) => Promise<readonly RuleMember[] | { members: readonly RuleMember[]; rep: number }>
  rep: (qid: string) => Promise<number>
}

export function projectedPageBlocks(
  content: PageContent,
  deps: RuleProjectionDeps,
  translationLanguage?: TranslationLanguage,
  /** The training card shown: a rule with authored clozes renders only that card (ruleCardBlocks). */
  card?: string,
): Promise<Blocks> {
  const base = pageBlocks(content, translationLanguage)
  if (content.kind !== 'note' && content.kind !== 'language')
    return Promise.resolve(base)
  return Promise.all(
    base.map(async (block) => {
      if (block.type === 'questionGroup') {
        const practice = groupPracticeQid(block)
        return Object.assign({}, block, { children: questionGroupCardBlocks(block, practice ? await deps.rep(practice) : 0, card) })
      }
      if (block.type !== 'rule') return block
      const rule = block as RuleBlock
      // A member rule's card is keyed by its persisted qid, never by its block id; authored practice items by their shared qid.
      const repQid = ruleHasClozes(rule) ? rulePracticeQid(rule.children) : ruleQid(rule)
      const rep = repQid ? await deps.rep(repQid) : 0
      const selected = await deps.members(rule.language, rule.key, rep)
      const selection = 'members' in selected ? selected : { members: selected, rep }
      // Copy: the same content object is projected repeatedly (once per rep),
      // so mutating the block would make every later rep see the first one.
      return Object.assign({}, block, {
        children: ruleCardBlocks(rule, selection.members, selection.rep, card),
      })
    }),
  )
}
